import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import Login from '../pages/auth/Login';
import Dashboard from '../pages/dashboard/Dashboard';
import Customers from '../pages/customers/Customers';
import CustomerDetails from '../pages/customers/CustomerDetails';
import Applications from '../pages/loans/Applications';
import ActiveLoans from '../pages/loans/ActiveLoans';
import LoanDetails from '../pages/loans/LoanDetails';
import LoanProducts from '../pages/loans/LoanProducts';
import Payments from '../pages/payments/Payments';
import Disbursements from '../pages/disbursements/Disbursements';
import Collections from '../pages/collections/Collections';
import Reports from '../pages/reports/Reports';
import AppLayout from '../components/layout/AppLayout';
import ProtectedRoute from './ProtectedRoute';
import { useAuth } from '../hooks/auth/useAuth';
import { CircularProgress, Box } from '@mui/material';

const RootRedirect: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return <Navigate to={isAuthenticated ? '/dashboard' : '/login'} replace />;
};

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<Login />} />

      {/* Protected Routes with Layout */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Dashboard />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* Customer Management */}
      <Route
        path="/customers"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Customers />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/customers/:id"
        element={
          <ProtectedRoute>
            <AppLayout>
              <CustomerDetails />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* Loan Management */}
      <Route
        path="/loans/applications"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Applications />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/loans/active"
        element={
          <ProtectedRoute>
            <AppLayout>
              <ActiveLoans />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/loans/:id"
        element={
          <ProtectedRoute>
            <AppLayout>
              <LoanDetails />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/loans/products"
        element={
          <ProtectedRoute>
            <AppLayout>
              <LoanProducts />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* Payment Processing */}
      <Route
        path="/payments"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Payments />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* Disbursements */}
      <Route
        path="/disbursements"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Disbursements />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* Collections */}
      <Route
        path="/collections"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Collections />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* Reports */}
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <AppLayout>
              <Reports />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* Default Redirect */}
      <Route path="/" element={<RootRedirect />} />

      {/* 404 Not Found */}
      <Route path="*" element={<RootRedirect />} />
    </Routes>
  );
};

export default AppRoutes;
